'use client'

/**
 * The fixed slot under the navbar that holds the site-wide sub-banners.
 *
 * 🚨 IT EXISTS TO OWN THE HORIZONTAL GEOMETRY. The slot used to be `fixed inset-x-0 top-16` in
 * layout.tsx — the full viewport width — while each banner centred its own `max-w-[1200px]` box on
 * that width. The left sidebar (72-368px) and the chat dock both paint over it, so the banner was
 * centred on the wrong axis and its CTA / dismiss button slid underneath the dock at ordinary
 * window sizes. It only looked survivable at 1920px, which is where it was built.
 *
 * The slot now spans exactly the content band: left = the live sidebar width (desktop only, the
 * sidebar is a drawer below md), right = `--alerts-chat-right-pad`, the same var ConditionalMain
 * and /alerts already use to reserve room for the dock. One source of truth for that edge.
 *
 * `data-sub-banner` is load-bearing: ConditionalMain measures this element and pads the page by its
 * height. Keep the attribute on the OUTER node.
 */

import type { ReactNode } from 'react'

export default function SubBannerSlot({ children }: { children: ReactNode }) {
  // ⚠ `left` is the --sidebar-width VAR, not React state. The var is right at server paint (the <head> pre-paint
  // script, src/lib/layout/railsPrePaint.ts: 0 below 768px, 72 or 368 above) and ConditionalMain keeps it current.
  // The old `isDesktop ? sidebarWidth : 0` started false, so on desktop the bar painted full width and jumped right
  // at hydration, and a narrower bar can wrap to a different HEIGHT, which moved the page under it
  // (register signed-in-visitors-still-shift-sitewide-1103).
  return (
    <div
      data-sub-banner=""
      // pointer-events-none on the slot + auto on each banner: the empty gutter above the page
      // never eats clicks meant for the content underneath.
      // ⚠ `top` follows the header rather than being a hard top-16, for the same reason the header
      // is no longer top-0: the alert banner stacks above both. ConditionalMain owns the var.
      className="pointer-events-none fixed z-20 transition-[left,right] duration-200 ease-out"
      style={{
        top: 'calc(4rem + var(--alert-banner-h, 0px))',
        left: 'var(--sidebar-width, 0px)',
        right: 'var(--alerts-chat-right-pad, 0px)',
      }}
    >
      {children}
    </div>
  )
}
